import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Plus, Wrench } from "lucide-react";
import { papi, errText } from "../portalApi";
import { Card, EmptyState, Field, Modal, PageHeader, Spinner, StatusBadge, fmtDate, inputCls } from "../ui";

const FINAL_APPROVER = "Chairman & MD";

function approvalTracker(r) {
  const hist = r.history || [];
  const last = (a) => [...hist].reverse().find((x) => x.action === a);
  if (r.status === "Pending") {
    return {
      cls: "text-amber-700",
      text: r.managerName ? `Awaiting approval from ${r.managerName}, then ${FINAL_APPROVER}` : `Awaiting approval from ${FINAL_APPROVER}`,
    };
  }
  if (r.status === "Manager Approved") {
    const h = last("Manager Approved");
    return { cls: "text-sky-700", text: `${h ? `Approved by ${h.by} · ` : ""}awaiting final approval from ${FINAL_APPROVER}` };
  }
  if (r.status === "Approved") {
    const h = last("Approved");
    return { cls: "text-emerald-700", text: h ? `Approved by ${h.by}` : "Approved" };
  }
  if (r.status === "Rejected") {
    const h = last("Rejected");
    return { cls: "text-rose-600", text: h ? `Rejected by ${h.by}${h.comment ? ` — “${h.comment}”` : ""}` : "Rejected" };
  }
  return null;
}

export default function Leave() {
  const [balances, setBalances] = useState(null);
  const [mine, setMine] = useState({ leaves: [], regularisations: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leaveTypeId: "", fromDate: "", toDate: "", halfDay: false, reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([papi.get("/leave/balances"), papi.get("/leave/my")]);
      setBalances(b.data);
      setMine(m.data);
    } catch (err) {
      toast.error(errText(err, "Could not load leave data."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!balances) return <Spinner />;

  const apply = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.post("/leave/apply", form);
      toast.success("Leave request submitted for approval.");
      setOpen(false);
      setForm({ leaveTypeId: "", fromDate: "", toDate: "", halfDay: false, reason: "" });
      load();
    } catch (err) {
      toast.error(errText(err, "Could not submit the request."));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try {
      await papi.post(`/leave/${id}/cancel`);
      toast.success("Leave request cancelled.");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not cancel the request."));
    }
  };

  const selBalance = balances.find((b) => b.leaveTypeId === form.leaveTypeId);

  return (
    <div data-testid="leave-page">
      <PageHeader title="Leave" sub="Your balances, applications and their approval status.">
        <button onClick={() => setOpen(true)} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="apply-leave-btn">
          <Plus className="h-4 w-4" aria-hidden /> Apply for Leave
        </button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="leave-balances">
        {balances.map((b) => (
          <Card key={b.leaveTypeId} testid={`balance-${b.code}`} className="!p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{b.name}</p>
            <p className="mt-2 font-serif text-3xl text-navy-900">
              {b.available}<span className="text-base text-slate-400"> / {b.allocated}</span>
            </p>
            <p className="mt-1 text-[10px] font-semibold text-slate-400">{b.paid ? "Paid" : "Unpaid"} · {b.used} used</p>
          </Card>
        ))}
      </div>

      <h2 className="mb-3 font-serif text-xl text-navy-900">My Requests</h2>
      {mine.leaves.length === 0 && mine.regularisations.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No requests yet" sub="Leave applications and attendance regularisations will appear here." testid="leave-empty" />
      ) : (
        <div className="space-y-3" data-testid="leave-history">
          {mine.leaves.map((l) => {
            const t = approvalTracker(l);
            return (
            <Card key={l.id} className="!p-4" testid={`leave-req-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-navy-900">{l.leaveTypeName}</p>
                    <StatusBadge status={l.status} />
                    {l.halfDay && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Half Day</span>}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {fmtDate(l.fromDate)} → {fmtDate(l.toDate)} · {l.days} day(s)
                    {l.managerName ? ` · Approver: ${l.managerName}` : ` · ${FINAL_APPROVER} approval`}
                  </p>
                  {t && (
                    <p className={`mt-1.5 flex items-center gap-1.5 text-xs font-semibold ${t.cls}`} data-testid={`leave-tracker-${l.id}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> {t.text}
                    </p>
                  )}
                  {l.reason && <p className="mt-1 text-xs italic text-slate-400">“{l.reason}”</p>}
                </div>
                {["Pending", "Manager Approved"].includes(l.status) && (
                  <button onClick={() => cancel(l.id)} className="text-xs font-semibold text-rose-600 transition-colors hover:text-rose-800" data-testid={`cancel-leave-${l.id}`}>
                    Cancel
                  </button>
                )}
              </div>
            </Card>
            );
          })}
          {mine.regularisations.map((r) => {
            const t = approvalTracker(r);
            return (
            <Card key={r.id} className="!p-4" testid={`reg-req-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Wrench className="h-4 w-4 text-gold-dark" aria-hidden />
                <p className="text-sm font-semibold text-navy-900">Attendance Regularisation</p>
                <StatusBadge status={r.status} />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {fmtDate(r.date)} · {r.issueType}
                {r.requestedCheckIn ? ` · ${r.requestedCheckIn}–${r.requestedCheckOut || "?"}` : ""}
              </p>
              {t && (
                <p className={`mt-1.5 flex items-center gap-1.5 text-xs font-semibold ${t.cls}`} data-testid={`reg-tracker-${r.id}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> {t.text}
                </p>
              )}
            </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Apply for Leave">
        <form onSubmit={apply} className="space-y-4" data-testid="leave-apply-form">
          <Field label="Leave Type">
            <select required className={inputCls} value={form.leaveTypeId} onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })} data-testid="leave-type-select">
              <option value="">Select leave type</option>
              {balances.map((b) => (
                <option key={b.leaveTypeId} value={b.leaveTypeId}>
                  {b.name} ({b.available} available)
                </option>
              ))}
            </select>
          </Field>
          {selBalance && selBalance.paid && selBalance.available === 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="leave-low-balance">
              You have no {selBalance.name} balance left — the request will be rejected unless it is unpaid leave.
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="From">
              <input type="date" required className={inputCls} value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} data-testid="leave-from-date" />
            </Field>
            <Field label="To">
              <input type="date" required className={inputCls} value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} data-testid="leave-to-date" />
            </Field>
          </div>
          {form.fromDate && form.fromDate === form.toDate && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.halfDay} onChange={(e) => setForm({ ...form, halfDay: e.target.checked })} className="h-4 w-4 accent-gold" data-testid="leave-half-day" />
              Half day
            </label>
          )}
          <Field label="Reason">
            <textarea required rows={3} className={inputCls} placeholder="Why do you need this leave?" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" />
          </Field>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="leave-submit">
            {saving ? "Submitting…" : "Submit Request"}
          </button>
        </form>
      </Modal>
    </div>
  );
}
